import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { ToolForm, ToolRunStatus, EvidencePackDownloads } from './ToolComponents';
import { ToolGate } from '../../components/ToolGate';
import { RepoPicker } from '../../components/RepoPicker';
import { GuestToolPanel } from '../../components/GuestToolPanel';
import { GuestConnectGitHub } from '../../components/GuestConnectGitHub';
import { GuestRepoPicker, GuestRepository } from '../../components/GuestRepoPicker';
import { UpgradeBanner } from '../../components/UpgradeBanner';
import { useToolReadiness } from '../../hooks/useToolReadiness';
import MarketingLayout from '../../components/MarketingLayout';
import Layout from '../../components/Layout';
import { ToolReadinessError } from '../../components/ToolReadinessError';
import { appLinks } from '../../lib/crossSiteLinks';
import { planLabels, resolveBannerMode } from '../../lib/billingUi';
import { getHubUrl } from '../../lib/apiBase';
import { branchProtectionApi, guestSessionApi, downloadApi } from '../../lib/toolsApi';
import { useAuthStore } from '../../stores/authStore';
import api from '../../lib/api';
import SEO from '../../components/SEO';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';
import { ToolConversionHeader } from '../../components/ToolConversionHeader';
import { toolsSeoIndexingEnabled } from '../../config/seo';
import { FileDropzone } from '../../components/FileDropzone';

/**
 * Branch Protection Snapshot & Drift Diff Tool Page
 * Creates point-in-time snapshots of branch protection settings and diffs against previous snapshots
 */
export const BranchProtectionPage: React.FC = () => {
  const readiness = useToolReadiness('branch-protection');
  const authHydrated = useAuthStore((state) => state._hasHydrated);
  const readinessAuth = readiness.data?.isAuthenticated;
  const storeAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthenticated = readinessAuth ?? (authHydrated ? storeAuthenticated : undefined);
  const isGuest = isAuthenticated === false;
  const tenantContext = useAuthStore((state) => state.tenantContext);
  const canFetchBillingStatus = isAuthenticated === true
    && (tenantContext?.role === 'Owner' || tenantContext?.role === 'Admin');
  const [activeTab, setActiveTab] = useState<'snapshot' | 'diff'>('snapshot');
  const [guestTab, setGuestTab] = useState<'snapshot' | 'diff'>('snapshot');
  const [status, setStatus] = useState<'idle' | 'generating' | 'completed' | 'error'>('idle');
  const [packId, setPackId] = useState<string>('');
  const [packStatusKey, setPackStatusKey] = useState<number>(0);
  const [latestPackStatus, setLatestPackStatus] = useState<string>('');
  const [checksum, setChecksum] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [signalRReady, setSignalRReady] = useState<boolean>(false);
  const pollingIntervalRef = useRef<number | null>(null);
  const connectionRef = useRef<HubConnection | null>(null);
  const [baselineFile, setBaselineFile] = useState<File | null>(null);
  
  // Diff-specific state
  const [diffFromDate, setDiffFromDate] = useState<string>('');
  const [diffToDate, setDiffToDate] = useState<string>('');
  const [includeAcknowledged, setIncludeAcknowledged] = useState<boolean>(false);
  const [diffCoverage, setDiffCoverage] = useState<{
    hasData: boolean;
    oldestSnapshotDate?: string;
    changeCount: number;
    trackedSince?: string;
    warning?: string;
  } | null>(null);

  // Guest diff session state
  interface GuestSessionDto {
    sessionId: string;
    expiresAt: string;
    hasGitHubConnection: boolean;
    selectedRepo: { id: number; fullName: string } | null;
    hasUsedReport: boolean;
    limitReached: boolean;
  }
  const [guestSession, setGuestSession] = useState<GuestSessionDto | null>(null);
  const [guestSessionLoading, setGuestSessionLoading] = useState(true);
  const [guestSessionError, setGuestSessionError] = useState<string | null>(null);
  const [guestSelectedRepoId, setGuestSelectedRepoId] = useState<number | null>(null);
  const [guestSelectedRepo, setGuestSelectedRepo] = useState<GuestRepository | null>(null);

  const { data: billingStatus } = useQuery({
    queryKey: ['billing', 'status', tenantContext?.tenantId],
    enabled: canFetchBillingStatus && !!tenantContext?.tenantId,
    queryFn: async () => {
      const response = await api.get('/v1/billing/status');
      return response.data as { planCode: string; planDisplayName: string; canShareEvidencePacks?: boolean };
    }
  });

  const bannerMode = resolveBannerMode(isAuthenticated === true, billingStatus?.planCode, billingStatus?.planDisplayName);
  const { packLimitLabel, retentionLabel } = planLabels(billingStatus?.planCode);

  const breadcrumbItems = [
    { name: 'Home', url: 'https://evidenttrail.com/' },
    { name: 'Tools', url: 'https://evidenttrail.com/tools' },
    { name: 'Branch Protection Snapshot', url: 'https://evidenttrail.com/tools/branch-protection-snapshot' }
  ];

  const faqs = [
    {
      question: 'Does this tool change branch protection settings?',
      answer: 'No. It only reads GitHub configuration metadata and produces an evidence pack.'
    },
    {
      question: 'Can I use guest mode?',
      answer: 'Yes. Guest mode supports one snapshot for one repository with 24-hour retention.'
    },
    {
      question: 'Does the report include diffs of code?',
      answer: 'No. The report is configuration metadata only and does not include source code.'
    },
    {
      question: 'Does this certify compliance?',
      answer: 'No. It provides evidence packs that support audit reviews; it does not certify compliance.'
    }
  ];

  const longformSections = [
    {
      title: 'What this free report includes',
      paragraphs: [
        'The branch protection snapshot captures GitHub configuration metadata for a selected branch and exports it as a tamper-evident pack.'
      ],
      bullets: [
        'Required review and code owner rules',
        'Required status checks and enforcement settings',
        'Admin enforcement and force-push settings',
        'CODEOWNERS presence metadata',
        'SHA-256 checksums with a manifest file'
      ]
    },
    {
      title: 'What auditors ask for',
      paragraphs: [
        'Auditors often request proof that branch protection settings were enabled during the audit window and that required checks were enforced.'
      ],
      bullets: [
        'Snapshot of protection rules for protected branches',
        'Evidence of required approvals and checks',
        'A reproducible export with checksums'
      ]
    },
    {
      title: 'How EvidentTrail works (read-only)',
      paragraphs: [
        'EvidentTrail uses read-only GitHub App access to read protection settings. No branch rules are modified.'
      ],
      bullets: [
        'Connect the read-only GitHub App',
        'Pick a repository and branch',
        'Export PDF, CSV, JSON, and manifest files'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Use the snapshot summary to show required checks and approvals at a point in time. Use it alongside approval reports for audit sampling.'
      ],
      bullets: [
        'Confirm required checks are listed',
        'Verify approvals and code owner rules',
        'Share the checksum with auditors'
      ]
    },
    {
      title: 'Boundaries (what we do not collect)',
      paragraphs: [
        'Snapshots are configuration metadata only. We do not store source code, prompts, or build logs.'
      ],
      bullets: [
        'No source code content or diffs',
        'No pull request descriptions or review comments',
        'No build logs or secrets'
      ]
    },
    {
      title: 'When to upgrade',
      paragraphs: [
        'Guest mode generates a one-off snapshot. Upgrading unlocks retention, multi-repo exports, and drift history.'
      ],
      bullets: [
        'Retain evidence packs beyond the guest window',
        'Export multi-repo snapshots in one pack',
        'Keep a drift history across audit windows'
      ]
    }
  ];

  // Repository selection state
  const [repoSelection, setRepoSelection] = useState<{
    installationId: string;
    organization: string;
    repositories: string[];
  } | null>(null);

  // Set default date range (last 30 days) when component mounts
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setDiffToDate(today.toISOString().split('T')[0]);
    setDiffFromDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  // Fetch drift coverage when repo selection or dates change in diff tab
  useEffect(() => {
    if (activeTab !== 'diff' || !repoSelection || repoSelection.repositories.length === 0) {
      setDiffCoverage(null);
      return;
    }

    const fetchCoverage = async () => {
      try {
        const repo = repoSelection.repositories[0]; // Single repo for diff
        const response = await api.get('/v1/tools/branch-protection/diff/coverage', {
          params: {
            organization: repoSelection.organization,
            repository: repo,
            branch: 'main'
          }
        });
        setDiffCoverage(response.data);
      } catch (err) {
        console.error('Failed to fetch diff coverage:', err);
      }
    };

    fetchCoverage();
  }, [activeTab, repoSelection]);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const normalizePackStatus = (value?: string) => (value ?? '').toString().toLowerCase();
  const isPackReady = (value?: string) => {
    const normalized = normalizePackStatus(value);
    return normalized === 'ready' || normalized === 'completed' || normalized === 'partiallimitreached' || normalized === 'partial_limit_reached';
  };

  // Poll evidence pack status
  const pollPackStatus = async (id: string) => {
    try {
      const response = isGuest 
        ? await api.get(`/v1/guest/evidence-packs/${id}`)
        : await api.get(`/v1/tools/evidence-packs/${id}`);
      
      const pack = response.data;
      setLatestPackStatus(pack.status || '');

      if (isPackReady(pack.status)) {
        setChecksum(pack.checksum || '');
        setStatus('completed');
        setProgressMessage('');
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else if (normalizePackStatus(pack.status) === 'failed') {
        setError(pack.errorMessage || 'Generation failed');
        setStatus('error');
        setProgressMessage('');
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (err) {
      console.error('Error polling pack status:', err);
    }
  };

  // Start polling when packId changes
  useEffect(() => {
    if (packId && (status === 'generating' || isGuest) && (!signalRReady || isGuest)) {
      pollPackStatus(packId);
      pollingIntervalRef.current = window.setInterval(() => {
        pollPackStatus(packId);
      }, 2000); // Poll every 2 seconds (fallback)
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [packId, status, signalRReady, isGuest, packStatusKey]);

  // SignalR subscription for real-time updates
  useEffect(() => {
    if (!packId || status !== 'generating' || isGuest) {
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(getHubUrl('/hubs/evidence'))
      .withAutomaticReconnect()
      .build();

    connection.on('PackProgress', (payload: any) => {
      setProgressMessage(payload.step || 'Generating...');
    });

    connection.on('PackCompleted', (payload: any) => {
      setChecksum(payload.checksum || '');
      setStatus('completed');
      setProgressMessage('');
      stopPolling();
    });

    connection.on('PackFailed', (payload: any) => {
      setError(payload.errorMessage || 'Generation failed');
      setStatus('error');
      setProgressMessage('');
      stopPolling();
    });

    connection
      .start()
      .then(() => connection.invoke('SubscribeToPack', packId))
      .then(() => setSignalRReady(true))
      .catch((err: unknown) => {
        console.warn('SignalR connection failed, falling back to polling', err);
        setSignalRReady(false);
      });

    connectionRef.current = connection;

    return () => {
      setSignalRReady(false);
      connection.stop().catch(() => undefined);
      stopPolling();
    };
  }, [packId, status, isGuest]);

  // Guest session management for diff tab
  const loadGuestSession = async () => {
    try {
      setGuestSessionLoading(true);
      setGuestSessionError(null);

      const data = await guestSessionApi.getSession();
      setGuestSession(data);
      setGuestSelectedRepoId(data.selectedRepo?.id ?? null);
    } catch (err) {
      setGuestSessionError(err instanceof Error ? err.message : 'Failed to load guest session');
    } finally {
      setGuestSessionLoading(false);
    }
  };

  const handleGuestRepoSelect = async (repo: GuestRepository) => {
    try {
      setError('');
      await guestSessionApi.selectRepository(repo.id);

      setGuestSelectedRepoId(repo.id);
      setGuestSelectedRepo(repo);
      setGuestSession((prev) => (prev ? { ...prev, selectedRepo: { id: repo.id, fullName: repo.fullName } } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select repository');
    }
  };

  // Load guest session when in guest mode and on diff tab
  useEffect(() => {
    if (isGuest && guestTab === 'diff') {
      void loadGuestSession();
    }
  }, [isGuest, guestTab]);

  const handleSnapshot = async (data: any) => {
    if (!repoSelection || repoSelection.repositories.length === 0) {
      setStatus('error');
      setError('Please select at least one repository');
      return;
    }

    setStatus('generating');
    setProgressMessage('Generating...');
    setError('');
    setLatestPackStatus('');
    setChecksum('');
    setPackStatusKey((prev) => prev + 1);

    try {
      const result = await branchProtectionApi.snapshot({
        installationId: repoSelection.installationId,
        organization: repoSelection.organization,
        repositories: repoSelection.repositories,
        branch: data.branch || 'main'
      });

      setPackId(result.packId);
      // Status remains 'generating' and polling will update it
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDiff = async (data: any) => {
    if (!repoSelection || repoSelection.repositories.length === 0) {
      setStatus('error');
      setError('Please select a repository');
      return;
    }

    setSignalRReady(false);
    setStatus('generating');
    setProgressMessage('Generating...');
    setError('');
    setLatestPackStatus('');
    setChecksum('');
    setPackStatusKey((prev) => prev + 1);

    try {
      const result = await branchProtectionApi.diff({
        installationId: repoSelection.installationId,
        organization: repoSelection.organization,
        repository: repoSelection.repositories[0], // Single repository
        branch: data.branch || 'main',
        fromDate: diffFromDate ? `${diffFromDate}T00:00:00Z` : undefined,
        toDate: diffToDate ? `${diffToDate}T23:59:59Z` : undefined,
        includeAcknowledged
      });

      setPackId(result.packId);
      pollPackStatus(result.packId);
      pollPackStatus(result.packId);
      // Status remains 'generating' and polling will update it
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleGuestDiff = async (_data: any) => {
    if (!baselineFile) {
      setStatus('error');
      setError('Please upload a baseline snapshot JSON file');
      return;
    }

    if (!guestSelectedRepo) {
      setStatus('error');
      setError('Please select a repository');
      return;
    }

    const organization = guestSelectedRepo.owner;
    const repository = guestSelectedRepo.name;

    setStatus('generating');
    setProgressMessage('Generating...');
    setError('');
    setLatestPackStatus('');
    setChecksum('');
    setPackStatusKey((prev) => prev + 1);

    try {
      const result = await branchProtectionApi.guestDiff(baselineFile, organization, repository);

      setPackId(result.packId);
      pollPackStatus(result.packId);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const toolUI = (
    <div className={isAuthenticated ? 'py-8' : 'max-w-[1140px] mx-auto px-4 sm:px-6 lg:px-8 py-12'}>
      {isGuest && (
        <div className="mb-8 rounded-2xl border border-slate-700/60 bg-slate-800/60 p-6 text-slate-200">
          <h3 className="text-lg font-semibold text-white mb-3">Guest mode quick start</h3>
          <ol className="space-y-2 text-sm text-slate-300">
            <li>1. Connect the read-only GitHub App.</li>
            <li>2. Select one repository for this session.</li>
            <li>3. Generate a branch protection snapshot evidence pack.</li>
          </ol>
          <p className="text-xs text-slate-400 mt-4">
            Guest sessions allow one report per repository with 24-hour retention.
          </p>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div id="snapshot-form" className="bg-white rounded-lg shadow-lg p-8">
            {readiness.error ? (
              <>
                <h2 className="text-2xl font-bold mb-6 text-gray-900">Create Snapshot</h2>
                <ToolReadinessError toolName="Branch Protection Tool" onRetry={() => readiness.refetch()} />
              </>
            ) : isGuest ? (
              <>
                <div className="flex space-x-4 mb-6 border-b border-gray-200">
                  <button
                    onClick={() => setGuestTab('snapshot')}
                    className={`pb-3 px-1 font-semibold transition ${guestTab === 'snapshot'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    Create Snapshot
                  </button>
                  <button
                    onClick={() => setGuestTab('diff')}
                    className={`pb-3 px-1 font-semibold transition ${guestTab === 'diff'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    Generate Diff
                  </button>
                </div>

                {guestTab === 'snapshot' && (
                  <>
                    <h2 className="text-2xl font-bold mb-6 text-gray-900">Create Snapshot</h2>
                    <GuestToolPanel
                      toolKey="branch-protection"
                      toolLabel="branch protection snapshot"
                      description="Guest mode supports branch protection snapshot exports. Drift-history diffs are available after signup."
                    />
                  </>
                )}

                {guestTab === 'diff' && (
                  <>
                    <h2 className="text-2xl font-bold mb-6 text-gray-900">Generate Diff from Upload</h2>

                    {guestSessionLoading ? (
                      <div className="border border-slate-700 rounded-lg p-6 bg-slate-800">
                        <div className="flex items-center justify-center gap-2 text-slate-400">
                          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                          <span>Loading guest session...</span>
                        </div>
                      </div>
                    ) : guestSessionError ? (
                      <div className="border border-red-200 rounded-lg p-6 bg-red-50 text-red-900">
                        <p className="font-semibold">Unable to load guest session</p>
                        <p className="text-sm mt-1">{guestSessionError}</p>
                        <button
                          onClick={() => void loadGuestSession()}
                          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
                        >
                          Retry
                        </button>
                      </div>
                    ) : !guestSession?.hasGitHubConnection ? (
                      <>
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900 mb-6">
                          <p className="font-semibold">Guest mode</p>
                          <p className="text-sm mt-1">Connect GitHub to run a single guest diff report for one repository.</p>
                        </div>
                        <GuestConnectGitHub onConnected={() => void loadGuestSession()} />
                      </>
                    ) : (
                      <>
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 mb-6">
                          <p className="font-semibold">Guest mode: one report, one repository</p>
                          <p className="text-sm mt-1">Upload a previous snapshot JSON to compare against the current settings.</p>
                        </div>

                        <div className="mb-6">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Select Repository *
                          </label>
                          <GuestRepoPicker selectedRepoId={guestSelectedRepoId} onSelect={handleGuestRepoSelect} />
                        </div>

                        <div className="mb-6">
                          <FileDropzone
                            label="Baseline snapshot JSON"
                            onFileSelected={(file) => {
                              setBaselineFile(file);
                            }}
                          />
                        </div>

                        <ToolForm onSubmit={handleGuestDiff} isLoading={status === 'generating'}>
                          <div className="text-sm text-gray-600">
                            The uploaded snapshot is validated against EvidentTrail or GitHub API schema before comparison.
                          </div>
                        </ToolForm>

                        <ToolRunStatus
                          status={status}
                          message={status === 'generating' ? (progressMessage || 'Processing diff...') : undefined}
                          error={error}
                        />

                        {(packId && (status === 'completed' || isPackReady(latestPackStatus))) && (
                          <EvidencePackDownloads
                            packId={packId}
                            checksum={checksum}
                            formats={isGuest ? ['pdf'] : ['pdf', 'csv', 'json', 'manifest']}
                            onDownload={(format: 'pdf' | 'csv' | 'json' | 'manifest') => {
                              window.location.href = downloadApi.getDownloadUrl(packId, format, isGuest);
                            }}
                            showShareButton={billingStatus?.canShareEvidencePacks ?? true}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <div className="flex space-x-4 mb-6 border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('snapshot')}
                    className={`pb-3 px-1 font-semibold transition ${activeTab === 'snapshot'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    Create Snapshot
                  </button>
                  <button
                    onClick={() => setActiveTab('diff')}
                    className={`pb-3 px-1 font-semibold transition ${activeTab === 'diff'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    Generate Diff
                  </button>
                </div>

                {activeTab === 'snapshot' && (
                  <>
                    <h2 className="text-2xl font-bold mb-6 text-gray-900">Create Snapshot</h2>
                    <p className="text-gray-600 mb-6">
                      Capture the current branch protection settings. Great for establishing a baseline or documenting current state.
                    </p>

                    {/* Repository Picker */}
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Select Repositories *
                      </label>
                      <RepoPicker
                        value={repoSelection}
                        onChange={setRepoSelection}
                        multiSelect={true}
                      />
                    </div>

                    <ToolForm onSubmit={handleSnapshot} isLoading={status === 'generating'}>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Branch
                        </label>
                        <input
                          type="text"
                          name="branch"
                          placeholder="main"
                          className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-900 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </ToolForm>
                  </>
                )}

                {activeTab === 'diff' && (
                  <>
                    <h2 className="text-2xl font-bold mb-6 text-gray-900">Generate Diff Report</h2>
                    <p className="text-gray-600 mb-6">
                      Compare branch protection changes detected by drift monitoring over a specific time period.
                    </p>

                    {/* Coverage Warning */}
                    {diffCoverage?.warning && (
                      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start">
                          <span className="text-yellow-600 mr-2">⚠️</span>
                          <div className="text-sm text-yellow-800">
                            <strong>Repository Not Tracked</strong>
                            <p className="mt-1">
                              {diffCoverage.warning}
                            </p>
                            <p className="mt-2">
                              Navigate to <a href="/drift" className="underline font-semibold hover:text-yellow-900">Drift Detection</a> to enable tracking for this repository.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {diffCoverage && !diffCoverage.hasData && !diffCoverage.warning && (
                      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start">
                          <span className="text-yellow-600 mr-2">⚠️</span>
                          <div className="text-sm text-yellow-800">
                            <strong>No drift data available</strong>
                            <p className="mt-1">
                              This repository doesn't have drift tracking data yet. 
                              Drift detection runs automatically when changes are detected.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {diffCoverage && diffCoverage.hasData && diffCoverage.oldestSnapshotDate && (
                      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-sm text-blue-800">
                          <strong>📊 Drift tracking active</strong>
                          <p className="mt-1">
                            Tracking since {new Date(diffCoverage.oldestSnapshotDate).toLocaleDateString()} 
                            {' '}• {diffCoverage.changeCount} change{diffCoverage.changeCount !== 1 ? 's' : ''} detected
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Repository Picker - Single select for diff */}
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Select Repository *
                      </label>
                      <RepoPicker
                        value={repoSelection}
                        onChange={setRepoSelection}
                        multiSelect={false}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Diff reports are generated for one repository at a time
                      </p>
                    </div>

                    {/* Date Range */}
                    <div className="mb-6 grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          From Date
                        </label>
                        <input
                          type="date"
                          value={diffFromDate}
                          onChange={(e) => setDiffFromDate(e.target.value)}
                          max={diffToDate}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          To Date
                        </label>
                        <input
                          type="date"
                          value={diffToDate}
                          onChange={(e) => setDiffToDate(e.target.value)}
                          min={diffFromDate}
                          max={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <ToolForm onSubmit={handleDiff} isLoading={status === 'generating'}>
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Branch
                        </label>
                        <input
                          type="text"
                          name="branch"
                          placeholder="main"
                          className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-900 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Include Acknowledged Changes */}
                      <div className="mb-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={includeAcknowledged}
                            onChange={(e) => setIncludeAcknowledged(e.target.checked)}
                            className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">
                            Include acknowledged changes
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 ml-6 mt-1">
                          Show changes that have been reviewed and acknowledged by your team
                        </p>
                      </div>
                    </ToolForm>
                  </>
                )}

                <ToolRunStatus
                  status={status}
                  message={status === 'generating' ? (progressMessage || 'Processing branch settings...') : undefined}
                  error={error}
                />

                {(packId && (status === 'completed' || isPackReady(latestPackStatus))) && (
                  <>
                    <EvidencePackDownloads
                      packId={packId}
                      checksum={checksum}
                      formats={isGuest ? ['pdf'] : ['pdf', 'csv', 'json', 'manifest']}
                      onDownload={(format: 'pdf' | 'csv' | 'json' | 'manifest') => {
                        window.location.href = downloadApi.getDownloadUrl(packId, format, isGuest);
                      }}
                      showShareButton={billingStatus?.canShareEvidencePacks ?? true}
                    />
                    <div className="mt-6">
                      <UpgradeBanner
                        mode={bannerMode}
                        planCode={billingStatus?.planCode}
                        planDisplayName={billingStatus?.planDisplayName}
                        packLimitLabel={packLimitLabel}
                        retentionLabel={retentionLabel}
                        variant="primary"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-3">Snapshot Captures</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✅ Required PR reviews</li>
              <li>✅ Code owner approval rules</li>
              <li>✅ Status check requirements</li>
              <li>✅ Admin enforcement status</li>
              <li>✅ CODEOWNERS file presence</li>
              <li>✅ Timestamp & SHA-256 checksums</li>
            </ul>
          </div>

          <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
            <h3 className="font-semibold text-gray-900 mb-3">Drift Detection</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>🔍 Compares snapshots</li>
              <li>⚠️ Severity classification</li>
              <li>📊 Shows field changes</li>
              <li>✔️ Added, removed, and modified rules</li>
            </ul>
          </div>

          <div className="bg-green-50 rounded-lg p-6 border border-green-200">
            <h3 className="font-semibold text-gray-900 mb-3">Use Cases</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>📋 Compliance audits</li>
              <li>🔒 Security reviews</li>
              <li>📈 Change approval trails</li>
              <li>⚙️ Policy drift detection</li>
            </ul>
          </div>
        </div>
      </div>

      {isAuthenticated === false && (
        <>
          {/* CTA Section */}
          <div className="mt-12 rounded-2xl border border-slate-700/60 bg-slate-800/60 p-6 text-slate-200 shadow-xl">
            <h3 className="text-xl font-bold text-white mb-3">
              Need a full drift history?
            </h3>
            <p className="text-slate-300 mb-4">
              The core platform stores branch protection drift events alongside the audit timeline and exception records.
            </p>
            <a
              href={appLinks.register}
              className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-500 transition font-semibold"
            >
              Explore the Core Platform →
            </a>
          </div>

          <div className="mt-10 grid md:grid-cols-6 gap-6">
            <Link to="/branch-protection-drift-detection" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">Branch Protection Drift Detection</h3>
              <p className="text-sm text-slate-300">See how drift detection maps to branch protection controls.</p>
            </Link>
            <Link to="/github-change-control" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">GitHub Change Control</h3>
              <p className="text-sm text-slate-300">Understand how protection rules support audit evidence.</p>
            </Link>
            <Link to="/tools" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">All Evidence Tools</h3>
              <p className="text-sm text-slate-300">Explore PR approvals and branch protection exports.</p>
            </Link>
            <Link to="/solutions" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">Solutions</h3>
              <p className="text-sm text-slate-300">Explore compliance-focused evidence workflows.</p>
            </Link>
            <Link to="/security" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">Security</h3>
              <p className="text-sm text-slate-300">Review read-only access and data minimization.</p>
            </Link>
            <Link to="/faq" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">FAQ</h3>
              <p className="text-sm text-slate-300">Common questions about evidence packs.</p>
            </Link>
          </div>
        </>
      )}
    </div>
  );

  const toolContent = isGuest || readiness.error || readiness.isLoading ? (
    toolUI
  ) : (
    <ToolGate toolKey="branch-protection" gateName="Branch Protection Tool">
      {toolUI}
    </ToolGate>
  );

  const guestPageContent = (
    <>
      {!toolsSeoIndexingEnabled && (
        <Helmet>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
      )}
      <BreadcrumbJsonLd items={breadcrumbItems} />
      <FaqJsonLd items={faqs} />
      <ToolConversionHeader
        title="Generate a branch protection snapshot"
        description="Use this snapshot to show required reviews and checks for protected branches during an audit window. The export is configuration metadata only and does not modify GitHub settings."
        primaryHref="#snapshot-form"
        secondaryHref="/sample-drift-event"
        secondaryLabel="See sample output"
      />

      <div className="max-w-3xl mx-auto mb-8 text-center">
        <Link to="/sample-drift-event" className="text-primary-400 hover:underline">
          View the full sample drift event {"->"}
        </Link>
        <div className="text-slate-500 text-sm mt-2">
          Learn more in our{' '}
          <Link to="/branch-protection-drift-detection" className="text-primary-400 hover:underline">
            branch protection drift detection guide
          </Link>.
        </div>
      </div>

      {toolContent}

      <SeoLongformSections sections={longformSections} faqs={faqs} />
    </>
  );

  if (isAuthenticated === undefined) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <Layout skipTenantRedirect>
        <SEO
          title="Branch Protection Snapshot & Drift Diff | EvidentTrail"
          description="Create point-in-time snapshots of branch protection rules and generate drift diffs. Export evidence packs with checksums."
        />
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-white mb-2">Branch Protection Snapshot</h1>
          <p className="text-slate-300">
            Capture branch protection configurations and generate snapshot or diff evidence packs.
          </p>
        </div>
        {toolContent}
      </Layout>
    );
  }

  return (
    <MarketingLayout
      title="Branch Protection Snapshot & Drift Diff | EvidentTrail"
      description="Create point-in-time snapshots of branch protection rules and generate drift diffs. Export evidence packs with checksums."
      h1="Branch Protection Snapshot"
      heroText="Capture branch protection configurations and generate snapshot or diff evidence packs."
      type="website"
    >
      {guestPageContent}
    </MarketingLayout>
  );
};

export default BranchProtectionPage;
